
/* eslint-disable react/prop-types */
import React, { cloneElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CropFree as CropFreeIcon } from '@mui/icons-material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { IconButton } from '@mui/material';
import { flowRight, isUndefined } from 'lodash';
import uuid from 'react-uuid';
import { SELECT_OPTION } from 'common/constants/inputs';
import useDataSheetDialogActions from 'common/hooks/useDataSheetDialogActions';
import { Dialog } from 'components/Dialogs';
import withRequired from 'components/WithRequired/withRequired';
import withTooltip from 'components/WithTooltip/withTooltip';
import { GridDialogValueViewerProps } from './GridDialogValueViewer.props';
import { classes, PREFIX, Root } from './GridDialogValueViewer.styles';

const GridDialogValueViewer = (props: GridDialogValueViewerProps) => {
  const { value, cell, options, onDoubleClick, disableBackdropClick: disableclick, isDisabled } = props;

  const customDialogRef = useRef<string>(uuid());
  const { setDialogFlag } = useDataSheetDialogActions();

  const [open, setOpen] = useState<boolean>(false);

  const openDialog = useCallback(() => {
    cell.disableEvents = true;
    setOpen(true);
  }, [cell]);

  const cellValue = useMemo(() => value ?? cell.value, [value, cell.value]);

  const showDialogIcon = useMemo(() => !(cell.dbType === 'boolean' && !Number(cell.value)), [cell.dbType, cell.value]);

  const disableBackdropClick = useMemo(() => (!isUndefined(disableclick) ? disableclick : true), [disableclick]);

  const handleClick = useCallback(() => {
    cell.disableEvents = false;
    if (onDoubleClick) {
      onDoubleClick();
    }
  }, [cell, onDoubleClick]);

  const closeDialog = useCallback(() => {
    setOpen(false);
  }, []);

  const selectedOption = useMemo(() => {
    if (Array.isArray(options) && cellValue?.toString()) {
      return options[cellValue as keyof typeof options];
    }

    return SELECT_OPTION;
  }, [options, cellValue]);

  useEffect(() => {
    const dialogId = customDialogRef.current;
    setDialogFlag({
      open: true,
      dialogId,
      cellId: cell.key,
      disableWhenLedgerOpen: false,
    });
    return () => {
      if (setDialogFlag) {
        setDialogFlag({ open: false, dialogId, cellId: null });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (cell.hidden) {
    return null;
  }
  return (
    <Root>
      <div className={classes.valueViewer}>
        <div
          id={`${cell.key}-date_viewer`}
          role="button"
          tabIndex={0}
          onClick={handleClick}
          onKeyDown={handleClick}
          className={`${classes.valueContainer} ${showDialogIcon ? classes.margin : ''} ${isDisabled ? classes.margin : ''}`.trim()}>
          <span>{String(selectedOption)}</span>
          {!isDisabled && (
            <span className={classes.dropdownIcon}>
              <ArrowDropDownIcon />
            </span>
          )}
        </div>
      </div>
      {showDialogIcon && (
        <div>
          <IconButton
            className={classes.actionBtn}
            disabled={cell.disabled}
            onClick={openDialog}
            size="small"
            color="primary">
            <CropFreeIcon
              id={`${cell.key}-cell_icon-value`}
              fontSize="small"
              color="secondary"
              className={classes.dialogIcon}
            />
          </IconButton>
        </div>
      )}
      {open && (
        <Dialog
          open={open}
          customDialogRef={customDialogRef.current}
          className="ledger-dialog"
          onClose={closeDialog}
          aria-labelledby="form-dialog-title"
          PaperProps={{
            square: true,
          }}
          hideTitle
          maxWidth={props.cell?.dialog?.maxWidth || 'md'}
          dialogProps={{
            disableBackdropClick,
            disableEscapeKeyDown: true,
            disableTitleClose: true,
            cellKey: props.cell.key,
            disableWhenLedgerOpen: props.cell.disableWhenLedgerOpen,
          }}>
          {cell.dialog?.content
            && React.isValidElement(cell.dialog.content)
            && React.cloneElement(cell.dialog.content, {
              closeDialog,
              cell,
              className: 'read-only--white',
            })}
        </Dialog>
      )}
    </Root>
  );
};

const enhance = flowRight(withTooltip, withRequired);

export default enhance(GridDialogValueViewer);
